import { useRef } from 'react';
import { 
  View, 
  Flex, 
  Link, 
  ListBox, 
  Item,
} from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import {
  AtollonButton,
  AtollonButtonDesignType,
} from '../../../../components/ui/Button/AtollonButton';
import FilterComponent from './FilterComponent';
import FilterSearchTextBox from './FilterSearchTextBox';
import CardComponentMobile from './CardComponentMobile';
import { Loader } from './Loader';
import { NoResultsMessage } from './NoResultsMessage';
import { MobileListViewProps } from '../types';
import styles from './MobileListView.module.css';
import serviceActivitiesStyles from '../service_activities.module.css';

/**
 * MobileListView Component
 * 
 * Mobile list view with filter, search, activity cards, and add button.
 * Displays activities in a scrollable list with filtering and search capabilities.
 */
export function MobileListView({
  activities,
  selectedKeys,
  isLoading,
  onActivitySelect,
  onLoadMore,
  onShowLess,
  isExpanded = false,
  hasMore = false,
  activityStatusList,
  onAddNew,
  onFilterChange,
  filterParams,
  searchedText,
  fpaTypeCategory,
  deviceInfo,
  customViews = [],
  hasMoreCustomViews = false,
  onLoadMoreCustomViews,
  onRefreshCustomViews,
  formSettings = [],
  pageView,
  mergedColumns = [],
  onTableRowClick,
  onTableRowDblClick,
  getStatusColor,
  isNumericValue,
  formatNumericValue,
  selectedApp,
  initialViewId,
  onCreateView,
}: MobileListViewProps) {
  const { t } = useTranslation();
  const listBoxRef = useRef<any>(null);

  const handleLoadMoreToggle = (): void => {
    if (isExpanded && onShowLess) {
      onShowLess();
    } else if (hasMore && onLoadMore && !isLoading) {
      onLoadMore();
    }
  };

  return (
    <View 
      width={'100%'} 
      height={'100%'} 
      UNSAFE_style={{ 
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Filter Component */}
      <View UNSAFE_style={{ height: '60px', flexShrink: 0, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <FilterComponent 
          key={selectedApp || 'default'}
          onFilterChange={onFilterChange as any} 
          filterParams={filterParams}
          customViews={customViews}
          hasMoreCustomViews={hasMoreCustomViews}
          onLoadMoreCustomViews={onLoadMoreCustomViews}
          onRefreshCustomViews={onRefreshCustomViews}
          formSettings={formSettings}
          fpaTypeCategory={fpaTypeCategory}
          initialViewId={initialViewId}
          onCreateView={onCreateView}
        />
      </View>
      
      {/* Search Component */}
      <View UNSAFE_style={{ height: '50px', flexShrink: 0, backgroundColor: 'white', padding: '5px 10px', borderBottom: '1px solid #e0e0e0' }}>
        <Flex direction={'row'} height={'40px'} alignItems={'center'} justifyContent={'end'} gap={'8px'}>
          <FilterSearchTextBox
            onSearchChange={onFilterChange as any}
            filterParams={filterParams}
            serachedText={searchedText}
            fpaTypeCategory={fpaTypeCategory}
          />
          <i className={`${styles.icon_search} bi bi-search`} />
        </Flex>
      </View>
      
      {/* Main Content Area - List or Table */}
      <View 
        flex={1}
        overflow={'auto'}
        UNSAFE_style={{ 
          padding: pageView === 'Detail' ? '0px' : '5px',
          backgroundColor: '#f5f6f9'
        }}
      >
        {isLoading ? (
          <View UNSAFE_style={{ padding: '20px' }}>
            <Loader />
          </View>
        ) : customViews.length === 0 ? (
          <Flex
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
            UNSAFE_style={{ padding: '20px' }}
          >
            <AtollonButton
              designType={AtollonButtonDesignType.PRIMARY}
              onPress={onCreateView}
              isDisabled={!onCreateView}
            >
              Add View
            </AtollonButton>
          </Flex>
        ) : activities?.length > 0 ? (
          // Card View (ListBox) - Always show cards view in mobile
          <ListBox
            ref={listBoxRef}
            id="listActivityCardsMobile"
            selectionMode="single"
            onSelectionChange={onActivitySelect}
            minHeight={'130px'}
            maxHeight={deviceInfo.shouldUseMobileLayout() ? `${deviceInfo.screenHeight}px` : '100%'}
            selectedKeys={selectedKeys}
            aria-label="Service Activities Cards Mobile"
            items={activities}
            isLoading={isLoading}
            onLoadMore={onLoadMore}
            UNSAFE_style={{ 
              width: '100%',
              backgroundColor: 'transparent'
            }}
          >
            {item => (
              <Item key={item.id} textValue={item.name}>
                <CardComponentMobile item={item} activityStatusList={activityStatusList} />
              </Item>
            )}
          </ListBox>
        ) : (
          <NoResultsMessage />
        )}
        {(isExpanded || (hasMore && !isExpanded)) && (
          <Flex
            width="100%"
            justifyContent="center"
            marginTop="size-100"
            paddingX="size-200"
          >
            <Link onPress={handleLoadMoreToggle} isQuiet>
              {isExpanded ? t('show_less', { ns: 'layout_components', defaultValue: 'Show Less' }) : t('load_more', { ns: 'layout_components', defaultValue: 'Load More' })}
            </Link>
          </Flex>
        )}
      </View>
      
      {/* Add New Button */}
      <View
        position={'absolute'}
        right={'16px'}
        bottom={'95px'}
        zIndex={10}
      >
        <Flex
          height={'48px'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={'8px'}
          UNSAFE_className={styles.add_new_parent}
        >
          <Link isQuiet onPress={onAddNew} UNSAFE_className={styles.add_new_btn}>
            <i className="bi bi-plus fs-5">
              <View UNSAFE_className={styles.add_new_btn_text}>{t('add_new', { ns: 'layout_components' })}</View>
            </i>
          </Link>
        </Flex>
      </View>
    </View>
  );
}

